const React = require('react');
const TextAlertsPreference = require('components/TextAlertsPreference');
const UserPreferences = require('components/UserPreferences');
const {
    useField,
    useFieldContext,
    usePreferencesHandle
} = require('./FieldContext');

const {
    useCallback,
    useEffect,
    useMemo,
    useRef
} = React;

const internals = {};

internals.SPECIAL_TEXT_ALERT_FIELDS = ['sms', 'phone', 'phoneCountry'];
internals.DEFAULT_REGULAR_FIELDS_TO_SHOW = [
    'profileEmail',
    'connectionsVisibility',
    'groupVisibility',
    'groupMsgNotifications',
    'digestInterval',
    'pendingConnections',
    'careerAlerts',
    'messageNotifications'
];
internals.DEFAULT_FIELDS_TO_SHOW = [
    'sms',
    ...internals.DEFAULT_REGULAR_FIELDS_TO_SHOW
];
internals.DEFAULT_HEADERS_TO_SHOW = [
    'communications',
    'connections',
    'groups',
    'emails',
    'other'
];

internals.shouldShowPreferences = (formProps = {}) => !!formProps.includePreferences;

internals.buildFieldsToShow = ({
    fieldsToShow
} = {}) => Array.isArray(fieldsToShow)
    ? fieldsToShow
    : internals.DEFAULT_FIELDS_TO_SHOW;

internals.shouldShowTextAlerts = ({
    fieldsToShow
} = {}) => internals.buildFieldsToShow({
    fieldsToShow
}).includes('sms');

internals.buildRegularFieldsToShow = ({
    fieldsToShow
} = {}) => {

    const resolvedFields = internals.buildFieldsToShow({
        fieldsToShow
    });

    return resolvedFields.filter((field) => !internals.SPECIAL_TEXT_ALERT_FIELDS.includes(field));
};

internals.buildHeadersToShow = ({
    headersToShow,
    fieldsToShow
} = {}) => {

    const resolvedHeaders = Array.isArray(headersToShow) ? headersToShow : internals.DEFAULT_HEADERS_TO_SHOW;
    const resolvedFields = internals.buildFieldsToShow({
        fieldsToShow
    });

    return resolvedHeaders.filter((header) => {

        if (header === 'communications') {
            return resolvedFields.includes('profileEmail');
        }

        return true;
    });
};

internals.shouldShowRegularPreferences = ({
    fieldsToShow,
    preferencesLoading
} = {}) => !preferencesLoading && internals.buildRegularFieldsToShow({
    fieldsToShow
}).length > 0;

module.exports = function PreferencesGroup(props = {}) {

    const {
        onPreferencesChange,
        preferencesFieldName = 'preferences'
    } = props;
    const fieldContext = useFieldContext();
    const preferencesHandle = usePreferencesHandle();
    const preferencesField = useField(preferencesFieldName);
    const preferencesRef = useRef(null);
    const textAlertsRef = useRef(null);
    const formProps = fieldContext?.formConfig?.context?.props || {};

    const setPreferenceControllerRef = useCallback((ref) => (nextPreferencesController) => {

        if (ref.current && ref.current !== nextPreferencesController) {
            preferencesHandle?.unregister(ref.current);
        }

        ref.current = nextPreferencesController;

        if (nextPreferencesController) {
            preferencesHandle?.register(nextPreferencesController);
        }
    }, [preferencesHandle]);

    const setPreferencesRef = useMemo(() => setPreferenceControllerRef(preferencesRef), [setPreferenceControllerRef]);
    const setTextAlertsRef = useMemo(() => setPreferenceControllerRef(textAlertsRef), [setPreferenceControllerRef]);

    useEffect(() => () => {

        preferencesHandle?.unregister(preferencesRef.current);
        preferencesHandle?.unregister(textAlertsRef.current);
    }, [preferencesHandle]);

    const onPreferencesSubmit = useCallback((preferences, setStateCb) => {

        preferencesField.setValue(preferences);

        if (typeof setStateCb === 'function') {
            setStateCb();
        }

        return Promise.resolve(preferences);
    }, [preferencesField]);

    const handlePreferencesChange = useCallback((field, value) => {

        if (typeof onPreferencesChange === 'function') {
            onPreferencesChange({
                field,
                formProps,
                value
            });
        }
    }, [formProps, onPreferencesChange]);

    const fieldsToShow = internals.buildFieldsToShow({
        fieldsToShow: formProps.preferencesFieldsToShow
    });
    const regularFieldsToShow = internals.buildRegularFieldsToShow({
        fieldsToShow
    });

    const preferencesProps = useMemo(() => ({
        preferences: formProps.signupDetails?.preferences,
        isSignupPage: formProps.mode === 'signup',
        isEditPage: formProps.mode === 'edit',
        onSubmit: onPreferencesSubmit,
        onSubmitNoRedirect: onPreferencesSubmit,
        onInvalidSubmit: formProps.onInvalidSubmit,
        onChange: handlePreferencesChange,
        headersToShow: internals.buildHeadersToShow({
            headersToShow: formProps.preferencesHeadersToShow,
            fieldsToShow: regularFieldsToShow
        }),
        fieldsToShow: regularFieldsToShow,
        hideJobAlerts: formProps.hideJobAlerts,
        useWrapper: false
    }), [
        formProps,
        handlePreferencesChange,
        onPreferencesSubmit,
        regularFieldsToShow
    ]);

    if (!internals.shouldShowPreferences(formProps)) {
        return null;
    }

    return <>
        {internals.shouldShowTextAlerts({ fieldsToShow }) ? <TextAlertsPreference
            ref={setTextAlertsRef}
            preferences={formProps.signupDetails?.preferences}
            isSignupPage={formProps.mode === 'signup'}
            isEditPage={formProps.mode === 'edit'}
            phoneRequired={!!formProps.requirePhoneForMfa}
            onChange={handlePreferencesChange}
        /> : null}
        {internals.shouldShowRegularPreferences({
            fieldsToShow,
            preferencesLoading: formProps.preferencesLoading
        }) ? <UserPreferences
            ref={setPreferencesRef}
            {...preferencesProps}
        /> : null}
    </>;
};

module.exports.internals = internals;
